import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import { inactivityTracker } from '../utils/inactivityTracker';
import InactivityWarningModal from '../components/InactivityWarningModal';
import { logout } from '../redux/counterSlice';
import { useLogoutUserMutation } from '../redux/apiSlice';
import { useNavigate } from 'react-router-dom';
import { useUserActivityPing } from '../hooks/useUserActivityPing';

interface InactivityContextType {
  sessionExpired: boolean;
  resetSessionExpired: () => void;
}

const InactivityContext = createContext<InactivityContextType | null>(null);

export const useInactivity = (): InactivityContextType => {
  const context = useContext(InactivityContext);
  if (!context) {
    throw new Error('useInactivity must be used within an InactivityProvider');
  }
  return context;
};

export const InactivityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useUserActivityPing();
  const [logoutUser, { isLoading }] = useLogoutUserMutation();
  const isAuthenticated = useSelector(
    (state: RootState) => state.app.user.isAuthenticated,
  );

  const [showWarning, setShowWarning] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(120); // 2 minutes in seconds

  const resetSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  const handleWarning = useCallback(() => {
    console.log('Showing inactivity warning modal with 2-minute countdown');
    setShowWarning(true);
    setRemainingSeconds(120); // Reset to 2 minutes (120 seconds)
  }, []);

  const handleLogout = useCallback(() => {
    console.log('Executing logout due to inactivity');
    setShowWarning(false);
    setSessionExpired(true);
    logoutUser();
    dispatch(logout());
    navigate('/login?expired=true');
  }, [dispatch, navigate]);

  const handleCloseWarning = useCallback(() => {
    setShowWarning(false);
    setRemainingSeconds(120); // Reset countdown
    inactivityTracker.continueSession();
  }, []);

  // useEffect(() => {
  //   if (isAuthenticated) {
  //     // Start tracking when user is authenticated
  //     inactivityTracker.startTracking(handleWarning, handleLogout);
  //   } else {
  //     // Stop tracking when user is not authenticated
  //     inactivityTracker.stopTracking();
  //   }

  //   return () => {
  //     // Clean up on unmount
  //     inactivityTracker.stopTracking();
  //   };
  // }, [isAuthenticated, handleWarning, handleLogout]);

  return (
    <InactivityContext.Provider value={{ sessionExpired, resetSessionExpired }}>
      {children}
      <InactivityWarningModal
        isOpen={showWarning}
        onClose={handleCloseWarning}
        remainingSeconds={remainingSeconds}
        onTimeout={handleLogout}
      />
    </InactivityContext.Provider>
  );
};

export default InactivityProvider;
